import { Card, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { chartColors } from '@utils/unifiedPlatformColorPalette';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { subdimensionId } from '@features/campaigns/subdomains/visitation/utils';
import { PerformersGroup } from './PerformersGroup';
import { OptimizationChart, OptimizationChartProps } from './OptimizationChart';
import { DimensionSelect } from './DimensionSelect';
import { LiftSelect, LiftSelectProps } from './LiftSelect';
import { PerformersCheckbox } from './PerformersCheckbox';
import { validDimensionSlug } from '@features/reports';
import { PerformersGroupProps } from './PerformersGroup';
import { BaseReport } from './OptimizationChart';
import { AIInsightsCard } from '@components/shared/AIInsightsCard';
import { AIInsights } from '@features/ai-insights';

const PERFORMER_COUNT = 3;
const ALL_DIMENSIONS_OPTION = {
  value: 'all',
  name: 'All Dimensions',
} as const;

export const OVERALL_OPTION = {
  value: 'overall',
  name: 'Overall',
  color: 'black',
} as const satisfies PerformersGroupProps['options'][0];

type BaseSubdimension = {
  statisticallySignificant: boolean;
  name: string;
  value: string;
};

const dimensionOptions = (subdimensions: Pick<BaseSubdimension, 'name'>[]) => {
  const dimensionNames = new Set(
    subdimensions.map(subdimension => subdimension.name)
  );
  return [
    ALL_DIMENSIONS_OPTION,
    ...[...dimensionNames]
      .filter(slug => validDimensionSlug(slug))
      .map(dimensionSlug => ({
        value: dimensionSlug,
        name: apiSlugToDimensionName[dimensionSlug],
      })),
  ];
};

export type ValueFromSubdimension<T> = (subdimension: T) => number | null;

const topBottomPerformers = <T extends BaseSubdimension>(
  subdimensions: T[],
  selectedDimension: string,
  valueFromSubdimension: ValueFromSubdimension<T>
) => {
  const orderedSubdimensions = subdimensions
    .filter(
      subdimension =>
        validDimensionSlug(subdimension.name) &&
        subdimension.statisticallySignificant &&
        (selectedDimension === ALL_DIMENSIONS_OPTION.value ||
          subdimension.name === selectedDimension)
    )
    .sort((a, b) => {
      const aValue = valueFromSubdimension(a);
      if (aValue === null) return 1;

      const bValue = valueFromSubdimension(b);
      if (bValue === null) return -1;

      return bValue - aValue;
    });

  const topPerformers = orderedSubdimensions
    .slice(0, PERFORMER_COUNT)
    .map((subdimension, index) => ({
      ...subdimension,
      color: chartColors[index],
    }));

  const bottomPerformersCount = Math.min(
    PERFORMER_COUNT,
    orderedSubdimensions.length - PERFORMER_COUNT
  );

  const bottomPerformers =
    bottomPerformersCount > 0
      ? orderedSubdimensions
          .slice(-bottomPerformersCount)
          .map((subdimension, index) => ({
            ...subdimension,
            color:
              chartColors[(index + topPerformers.length) % chartColors.length],
          }))
      : [];

  return {
    topPerformers,
    bottomPerformers,
  };
};

export type PerformersCardProps<T, U, V extends string> = {
  reports: T[];
  reportDates: OptimizationChartProps<T, U>['reportDates'];
  currentSubdimensions: U[];
  getValueByLiftType: Record<V, ValueFromSubdimension<U>>;
  getOverallValueByLiftType: Record<V, (report: T) => number | null>;
  getSubdimensionsFromReport: OptimizationChartProps<
    T,
    U
  >['getSubdimensionsFromReport'];
  defaultLiftType: V;
  liftOptions: (LiftSelectProps<V>['options'][number] & {
    chartLabel: string;
  })[];
  filterReports?: (liftType: V) => T[];
  aiInsights: AIInsights | null;
};

export const PerformersCard = <
  T extends BaseReport,
  U extends BaseSubdimension,
  V extends string,
>(
  props: PerformersCardProps<T, U, V>
) => {
  const [selectedDimension, setSelectedDimension] = useState<string>(
    ALL_DIMENSIONS_OPTION.value
  );
  const [selectedLiftType, setSelectedLiftType] = useState<V>(
    props.defaultLiftType
  );
  const valueFromSubdimension: ValueFromSubdimension<U> =
    props.getValueByLiftType[selectedLiftType];

  const { topPerformers, bottomPerformers } = topBottomPerformers(
    props.currentSubdimensions,
    selectedDimension,
    valueFromSubdimension
  );

  const [selectedSubdimensions, setSelectedSubdimensions] = useState<
    Record<string, boolean>
  >(
    Object.fromEntries(
      [OVERALL_OPTION, ...topPerformers, ...bottomPerformers].map(
        subdimension => [subdimensionId(subdimension), true]
      )
    )
  );

  const onChangeSubdimension = (
    name: string,
    value: string,
    checked: boolean
  ) => {
    setSelectedSubdimensions({
      ...selectedSubdimensions,
      [subdimensionId({ name, value })]: checked,
    });
  };

  const selectedLiftOption =
    props.liftOptions.find(option => option.value === selectedLiftType) ??
    props.liftOptions[0];

  return (
    <Card sx={{ padding: '1rem' }}>
      <Stack direction="column" spacing="1rem">
        <Typography variant="h5">Top and Bottom Performers</Typography>

        <AIInsightsCard
          testId="performers-card-ai-insights"
          aiInsights={props.aiInsights ?? undefined}
          highlights={null}
        />

        <Stack direction="row" spacing="1rem">
          <Stack direction="column" spacing="1rem" minWidth={300}>
            <DimensionSelect
              options={dimensionOptions(props.currentSubdimensions)}
              onChange={setSelectedDimension}
            />
            {props.liftOptions.length > 1 ? (
              <LiftSelect
                value={selectedLiftOption.value}
                onChange={setSelectedLiftType}
                options={props.liftOptions}
              />
            ) : null}
            <PerformersCheckbox
              option={OVERALL_OPTION}
              onChange={(_e, checked) =>
                onChangeSubdimension(
                  OVERALL_OPTION.name,
                  OVERALL_OPTION.value,
                  checked
                )
              }
              label="Overall"
            />
            <PerformersGroup
              type="top"
              options={topPerformers}
              onChange={onChangeSubdimension}
            />
            <PerformersGroup
              type="bottom"
              options={bottomPerformers}
              onChange={onChangeSubdimension}
            />
          </Stack>
          <OptimizationChart
            selectedSubdimensions={selectedSubdimensions}
            reports={props.filterReports?.(selectedLiftType) ?? props.reports}
            reportDates={props.reportDates}
            subdimensions={[...topPerformers, ...bottomPerformers]}
            yAxisLabel={selectedLiftOption.chartLabel}
            getValueFromSubdimension={valueFromSubdimension}
            getValueFromReport={
              props.getOverallValueByLiftType[selectedLiftOption.value]
            }
            getSubdimensionsFromReport={props.getSubdimensionsFromReport}
          />
        </Stack>
      </Stack>
    </Card>
  );
};
